/**
 * The working copy of a mapping configuration's field mappings and their
 * transform chains, as the Map and Transform screens edit it, and the rules
 * for turning it back into what `data-mapper-mapping-configs` accepts.
 *
 * Child ids (data-mapper-mapping-configs.class.ts `keepOwnIds`):
 *   - a CREATE sends no child ids at all; the API mints them;
 *   - a SAVE of an existing config sends back only the ids the API gave that
 *     config's children (the draft keeps them from the `get`), and none for a
 *     child added since. The API would re-mint a foreign id anyway; not sending
 *     one keeps the request honest and inside `^[A-Za-z0-9_-]+$`.
 * `order` is the chain position, an integer (an MSSQL INT on the API).
 */
import { CHILD_ID_PATTERN, type DataMapperFieldMapping, type DataMapperTransformStep } from "../dataMapper.types";
import type { TransformConfig, TransformType } from "../engine/types";

export interface DraftTransform {
  /** Local key for React and selection; never sent. */
  key: string;
  /** The API's id for this step, when it came from the loaded config. */
  id: string | null;
  type: TransformType;
  config: TransformConfig;
  enabled: boolean;
}

export interface DraftMapping {
  key: string;
  id: string | null;
  sourceTable: string;
  sourceColumn: string;
  targetField: string;
  isAutoMapped: boolean;
  confidence: number | null;
  notes: string | null;
  transforms: DraftTransform[];
}

let keySeq = 0;
/** A local key, unique for the page's life. */
export const draftKey = (prefix: string): string => `${prefix}-${++keySeq}`;

/** An id the API gave, if it is one the API will accept back. */
export const ownChildId = (id: unknown): string | null =>
  typeof id === "string" && CHILD_ID_PATTERN.test(id) ? id : null;

/** The loaded config's mappings as a draft: transforms in chain order, ids kept. */
export function toDraft(mappings: readonly DataMapperFieldMapping[] | undefined): DraftMapping[] {
  return (mappings ?? []).map((m) => ({
    key: draftKey("m"),
    id: ownChildId(m.id),
    sourceTable: m.sourceTable ?? "",
    sourceColumn: m.sourceColumn ?? "",
    targetField: m.targetField,
    isAutoMapped: m.isAutoMapped === true,
    confidence: typeof m.confidence === "number" ? m.confidence : null,
    notes: m.notes ?? null,
    transforms: [...(m.transforms ?? [])]
      .map((t, i) => ({ t, i }))
      .sort((a, b) => (a.t.order ?? a.i) - (b.t.order ?? b.i) || a.i - b.i)
      .map(({ t }) => ({
        key: draftKey("t"),
        id: ownChildId(t.id),
        type: t.type,
        config: (t.config ?? { type: t.type }) as TransformConfig,
        enabled: t.enabled !== false,
      })),
  }));
}

/**
 * The draft as the API's `mappings`. `keepIds` false (a create) sends no ids;
 * true (a save of the loaded config) sends the ids that came from it.
 */
export function toPayload(draft: readonly DraftMapping[], { keepIds }: { keepIds: boolean }): DataMapperFieldMapping[] {
  return draft.map((m) => {
    const transforms: DataMapperTransformStep[] = m.transforms.map((t, order) => ({
      ...(keepIds && t.id ? { id: t.id } : {}),
      type: t.type,
      config: t.config,
      order,
      enabled: t.enabled,
    }));
    return {
      ...(keepIds && m.id ? { id: m.id } : {}),
      sourceTable: m.sourceTable,
      sourceColumn: m.sourceColumn,
      targetField: m.targetField,
      isAutoMapped: m.isAutoMapped,
      confidence: m.confidence,
      notes: m.notes,
      transforms,
    };
  });
}

/** `items` with the one at `from` moved to `to` (a new array; out-of-range is a no-op copy). */
export function moveItem<T>(items: readonly T[], from: number, to: number): T[] {
  const next = [...items];
  if (from < 0 || from >= next.length || to < 0 || to >= next.length || from === to) return next;
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
}

/** The same mapping in a reloaded draft (ids are re-minted for new rows, and the API reorders). */
export function sameMapping(a: Pick<DraftMapping, "targetField" | "sourceTable" | "sourceColumn">, b: typeof a): boolean {
  return a.targetField === b.targetField && a.sourceTable === b.sourceTable && a.sourceColumn === b.sourceColumn;
}
